import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Card, Row, Col, Table, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import * as ChartJS from 'chart.js';

import { rangeDateReport, messageToast } from '@app/utils/define';
import { baoCaoGetTongQuanTeamAction } from '@app/store/reducers/baocao';

import BaoCaoChiTietXuLyYeuCau from '@app/pages/baocao/BaoCaoChiTietXuLyYeuCau';
import BaoCaoChiTietHoTro from '@app/pages/baocao/BaoCaoChiTietHoTro';
import BaoCaoChiTietNhiemVuKhac from '@app/pages/baocao/BaoCaoChiTietNhiemVuKhac';
import BaoCaoChiTietPhatTrienPhanMem from '@app/pages/baocao/BaoCaoChiTietPhatTrienPhanMem';

import { URI_INTERNAL } from '@app/utils/pathLocations';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const RANGE_CUSTOM = 'Custom Range';

const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return {
    from: toYMD(pair[0]),
    to: toYMD(pair[1]),
  };
};

const inferRangeLabelFromDates = (from, to) => {
  const f = from || '';
  const t = to || '';

  if (!f && !t) return '';

  for (const option of RANGE_OPTIONS) {
    if (!option || option === RANGE_CUSTOM) continue;

    const range = getRangeFromTo(option);
    if (range.from === f && range.to === t) {
      return option;
    }
  }

  return RANGE_CUSTOM;
};

const getInitialModuleRangeState = (search) => {
  const params = new URLSearchParams(search || '');
  const from = params.get('From') || '';
  const to = params.get('To') || '';

  if (from || to) {
    return {
      label: inferRangeLabelFromDates(from, to),
      fromDate: from,
      toDate: to,
      appliedFrom: from,
      appliedTo: to,
    };
  }

  const fallback = getRangeFromTo(DEFAULT_COMMON_RANGE_LABEL);

  return {
    label: DEFAULT_COMMON_RANGE_LABEL,
    fromDate: fallback.from,
    toDate: fallback.to,
    appliedFrom: fallback.from,
    appliedTo: fallback.to,
  };
};

const getInitialCommonRangeState = (search) => {
  const params = new URLSearchParams(search || '');
  const from = params.get('From') || '';
  const to = params.get('To') || '';

  if (from || to) {
    return {
      label: inferRangeLabelFromDates(from, to),
      fromDate: from,
      toDate: to,
      appliedFrom: from,
      appliedTo: to,
    };
  }

  const fallback = getRangeFromTo(DEFAULT_COMMON_RANGE_LABEL);

  return {
    label: DEFAULT_COMMON_RANGE_LABEL,
    fromDate: fallback.from,
    toDate: fallback.to,
    appliedFrom: fallback.from,
    appliedTo: fallback.to,
  };
};

const ChartCtor = ChartJS?.Chart || ChartJS?.default || ChartJS;
if (ChartCtor && typeof ChartCtor.register === 'function' && Array.isArray(ChartJS.registerables)) {
  ChartCtor.register(...ChartJS.registerables);
}

/* Chart 1: Stacked bar theo nhóm  */
const StackedBarTeamChart = ({ rows = [], loading = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const chartPayload = useMemo(() => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const labels = safeRows.map((r) => (r?.tenTeam ? String(r.tenTeam) : `Team ${r?.teamId ?? ''}`));

    const dataPhanMem = safeRows.map((r) => toNum(r?.tongPhanMem));
    const dataXuLy = safeRows.map((r) => toNum(r?.tongXuLyYeuCau));
    const dataHoTro = safeRows.map((r) => toNum(r?.tongHoTro));
    const dataNVK = safeRows.map((r) => toNum(r?.tongNhiemVuKhac));

    return {
      labels,
      datasets: [
        {
          label: 'Phần mềm',
          data: dataPhanMem,
          backgroundColor: '#2563eb',
          borderColor: '#2563eb',
          borderWidth: 1,
        },
        {
          label: 'Xử lý yêu cầu',
          data: dataXuLy,
          backgroundColor: '#f59e0b',
          borderColor: '#f59e0b',
          borderWidth: 1,
        },
        {
          label: 'Hỗ trợ',
          data: dataHoTro,
          backgroundColor: '#10b981',
          borderColor: '#10b981',
          borderWidth: 1,
        },
        {
          label: 'Nhiệm vụ khác',
          data: dataNVK,
          backgroundColor: '#ef4444',
          borderColor: '#ef4444',
          borderWidth: 1,
        },
      ],
    };
  }, [rows]);

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {
        // ignore
      }
      chartRef.current = null;
    }

    if (loading) return;
    if (!canvasRef.current) return;
    if (!chartPayload?.labels?.length) return;
    if (!ChartCtor) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const isV3Plus = !!(ChartCtor && typeof ChartCtor.register === 'function');

    const optionsV3 = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            footer: (tooltipItems) => {
              const total = (tooltipItems || []).reduce((sum, it) => sum + toNum(it?.parsed?.y), 0);
              return `Tổng: ${total}`;
            },
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { autoSkip: false, maxRotation: 0, minRotation: 0 } },
        y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
      },
    };

    const optionsV2 = {
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          footer: (tooltipItems) => {
            const total = (tooltipItems || []).reduce((sum, it) => sum + toNum(it?.yLabel), 0);
            return `Tổng: ${total}`;
          },
        },
      },
      legend: { position: 'bottom' },
      scales: {
        xAxes: [
          {
            stacked: true,
            gridLines: { display: false },
            ticks: { autoSkip: false },
          },
        ],
        yAxes: [
          {
            stacked: true,
            ticks: { beginAtZero: true, precision: 0 },
          },
        ],
      },
    };

    chartRef.current = new ChartCtor(ctx, {
      type: 'bar',
      data: chartPayload,
      options: isV3Plus ? optionsV3 : optionsV2,
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {
          // ignore
        }
        chartRef.current = null;
      }
    };
  }, [chartPayload, loading]);

  return (
    <div className="card h-100">
      <div className="card-header">
        <div className="d-flex align-items-center justify-content-between">
          <div className="card-title mb-0" style={{ fontWeight: 700 }}>
            Biểu đồ cột theo nhóm
          </div>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <div>Đang tải...</div>
        ) : !chartPayload?.labels?.length ? (
          <div className="text-muted">Không có dữ liệu để hiển thị biểu đồ.</div>
        ) : (
          <div style={{ height: 340 }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </div>
    </div>
  );
};

/* Chart 2: Donut cơ cấu theo loại công việc */
const WorkStructureDonutChart = ({ rows = [], loading = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const totals = useMemo(() => {
    const safeRows = Array.isArray(rows) ? rows : [];
    let phanMem = 0;
    let xuLy = 0;
    let hoTro = 0;
    let nvk = 0;

    safeRows.forEach((r) => {
      phanMem += toNum(r?.tongPhanMem);
      xuLy += toNum(r?.tongXuLyYeuCau);
      hoTro += toNum(r?.tongHoTro);
      nvk += toNum(r?.tongNhiemVuKhac);
    });

    const total = phanMem + xuLy + hoTro + nvk;
    return { phanMem, xuLy, hoTro, nvk, total };
  }, [rows]);

  const chartPayload = useMemo(() => {
    return {
      labels: ['Phần mềm', 'Xử lý yêu cầu', 'Hỗ trợ', 'Nhiệm vụ khác'],
      datasets: [
        {
          data: [totals.phanMem, totals.xuLy, totals.hoTro, totals.nvk],
          backgroundColor: ['#2563eb', '#f59e0b', '#10b981', '#ef4444'],
          borderColor: ['#2563eb', '#f59e0b', '#10b981', '#ef4444'],
          borderWidth: 1,
        },
      ],
    };
  }, [totals]);

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {
        // ignore
      }
      chartRef.current = null;
    }

    if (loading) return;
    if (!canvasRef.current) return;
    if (!ChartCtor) return;
    if (!totals.total) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const isV3Plus = !!(ChartCtor && typeof ChartCtor.register === 'function');

    const optionsV3 = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (it) => {
              const value = toNum(it?.parsed);
              const pct = totals.total ? Math.round((value / totals.total) * 100) : 0;
              const label = it?.label || '';
              return `${label}: ${value} (${pct}%)`;
            },
          },
        },
      },
      cutout: '60%',
    };

    const optionsV2 = {
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: 'bottom' },
      tooltips: {
        callbacks: {
          label: (tooltipItem, data) => {
            const idx = tooltipItem.index;
            const label = (data.labels && data.labels[idx]) || '';
            const value = toNum(data?.datasets?.[0]?.data?.[idx]);
            const pct = totals.total ? Math.round((value / totals.total) * 100) : 0;
            return `${label}: ${value} (${pct}%)`;
          },
        },
      },
      cutoutPercentage: 60,
    };

    chartRef.current = new ChartCtor(ctx, {
      type: 'doughnut',
      data: chartPayload,
      options: isV3Plus ? optionsV3 : optionsV2,
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {
          // ignore
        }
        chartRef.current = null;
      }
    };
  }, [chartPayload, loading, totals, totals.total]);

  return (
    <div className="card h-100">
      <div className="card-header">
        <div className="card-title mb-0" style={{ fontWeight: 700 }}>
          Cơ cấu công việc
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <div>Đang tải...</div>
        ) : !totals.total ? (
          <div className="text-muted">Không có dữ liệu để hiển thị biểu đồ.</div>
        ) : (
          <div style={{ height: 340 }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </div>
    </div>
  );
};

const DEFAULT_COMMON_RANGE_LABEL = '7 ngày trước';

const BaoCaoTongQuanTeam = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { tongQuanTeam, loading, error } = useSelector((s) => s.baocao || {});
  const [includeChuaGanNhom] = useState(true);

  const initialCommonRange = useMemo(
  () => getInitialCommonRangeState(location.search),
  [location.search]
);

const initialModuleRange = useMemo(
  () => getInitialModuleRangeState(location.search),
  [location.search]
);

const [commonRangeLabel, setCommonRangeLabel] = useState(initialCommonRange.label);

const [commonFromDate, setCommonFromDate] = useState(initialCommonRange.fromDate);
const [commonToDate, setCommonToDate] = useState(initialCommonRange.toDate);

const [commonAppliedFrom, setCommonAppliedFrom] = useState(initialCommonRange.appliedFrom);
const [commonAppliedTo, setCommonAppliedTo] = useState(initialCommonRange.appliedTo);

const [rangeLabel, setRangeLabel] = useState(initialModuleRange.label);
const [fromDate, setFromDate] = useState(initialModuleRange.fromDate);
const [toDate, setToDate] = useState(initialModuleRange.toDate);

const [appliedFrom, setAppliedFrom] = useState(initialModuleRange.appliedFrom);
const [appliedTo, setAppliedTo] = useState(initialModuleRange.appliedTo);

  const [commonVersion, setCommonVersion] = useState(1);

  const applyCommonToModule = (nextLabel, nextFrom, nextTo) => {
    const l = nextLabel || '';
    const f = nextFrom || '';
    const t = nextTo || '';

    setRangeLabel(l);
    setFromDate(f);
    setToDate(t);
    setAppliedFrom(f);
    setAppliedTo(t);

    setCommonVersion((v) => v + 1);
  };

  const handleChangeCommonRange = (e) => {
    const v = e.target.value || '';
    setCommonRangeLabel(v);

    if (v && v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);

      setCommonFromDate(from);
      setCommonToDate(to);
      setCommonAppliedFrom(from);
      setCommonAppliedTo(to);

      applyCommonToModule(v, from, to);
      return;
    }

    if (v === RANGE_CUSTOM) {
      setCommonFromDate(commonAppliedFrom || '');
      setCommonToDate(commonAppliedTo || '');
      return;
    }

    setCommonFromDate('');
    setCommonToDate('');
    setCommonAppliedFrom('');
    setCommonAppliedTo('');

    applyCommonToModule('', '', '');
  };

  const handleApplyCommonCustomRange = () => {
    if (commonRangeLabel === RANGE_CUSTOM && !commonFromDate && !commonToDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }

    const f = commonFromDate || '';
    const t = commonToDate || '';

    setCommonAppliedFrom(f);
    setCommonAppliedTo(t);

    applyCommonToModule(RANGE_CUSTOM, f, t);
  };

  const handleClearCommonFilter = () => {
    setCommonRangeLabel('');
    setCommonFromDate('');
    setCommonToDate('');
    setCommonAppliedFrom('');
    setCommonAppliedTo('');

    applyCommonToModule('', '', '');
  };

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (!v) {
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
      return;
    }

    if (v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      setFromDate(from);
      setToDate(to);
      setAppliedFrom(from);
      setAppliedTo(to);
      return;
    }

    setFromDate(appliedFrom || '');
    setToDate(appliedTo || '');
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    setAppliedFrom(fromDate || '');
    setAppliedTo(toDate || '');
  };

  const handleClearFilter = () => {
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    setAppliedFrom('');
    setAppliedTo('');
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const nextFrom = appliedFrom || '';
    const nextTo = appliedTo || '';

    if (nextFrom) params.set('From', nextFrom);
    else params.delete('From');

    if (nextTo) params.set('To', nextTo);
    else params.delete('To');

    const nextSearch = params.toString();
    const currentSearch = (location.search || '').replace(/^\?/, '');

    if (nextSearch !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      });
    }
  }, [history, location.pathname, location.search, appliedFrom, appliedTo]);

  useEffect(() => {
    dispatch(
      baoCaoGetTongQuanTeamAction({
        includeChuaGanNhom,
        From: appliedFrom || '',
        To: appliedTo || '',
      })
    );
  }, [dispatch, includeChuaGanNhom, appliedFrom, appliedTo]);

  useEffect(() => {
    if (!error) return;
    toast.error(getErrMsg(error) || 'Không thể tải dữ liệu báo cáo. Vui lòng thử lại.');
  }, [error]);

  const rows = useMemo(() => {
    if (!Array.isArray(tongQuanTeam)) return [];

    return tongQuanTeam.map((x) => {
      const teamId = x?.teamId ?? x?.TeamId ?? 0;
      const tenTeam = x?.tenTeam ?? x?.TenTeam ?? '';

      const tongPhanMem = toNum(x?.tongPhanMem ?? x?.TongPhanMem ?? 0);
      const tongXuLyYeuCau = toNum(x?.tongXuLyYeuCau ?? x?.TongXuLyYeuCau ?? 0);
      const tongHoTro = toNum(x?.tongHoTro ?? x?.TongHoTro ?? 0);
      const tongNhiemVuKhac = toNum(x?.tongNhiemVuKhac ?? x?.TongNhiemVuKhac ?? 0);

      return {
        teamId,
        tenTeam,
        tongPhanMem,
        tongXuLyYeuCau,
        tongHoTro,
        tongNhiemVuKhac,
        tongCong: tongPhanMem + tongXuLyYeuCau + tongHoTro + tongNhiemVuKhac,
      };
    });
  }, [tongQuanTeam]);

  const detailBasePath =
    URI_INTERNAL?.BAOCAO_TONG_QUAN_NHAN_VIEN_PATH ||
    `${URI_INTERNAL.BAOCAO_THONGKE_PATH}/tong-quan-nhan-vien`;

  const currentReturnUrl = useMemo(
    () => encodeURIComponent(`${location.pathname}${location.search || ''}`),
    [location.pathname, location.search]
  );

  const renderFilterHint = (from, to) => {
    if (!from && !to) return null;
    return (
      <div className="filter-hint" style={{ width: '100%' }}>
        Đang lọc : {fmtDDMMYYYY(from) || '...'} → {fmtDDMMYYYY(to) || '...'}
      </div>
    );
  };
  return (
    <div className="container-fluid" style={{ marginTop: 16 }}>
      <Row className="mb-3">
        <Col lg={12}>
          <Card>
            <Card.Body>
              <div className="d-flex flex-column align-items-start">
                <div className="d-flex flex-wrap align-items-end gap-2">
                  <div style={{ minWidth: 260 }}>
                    <label className="form-label mb-1">Khoảng thời gian</label>
                    <select
                      className="form-select form-select-sm"
                      value={commonRangeLabel}
                      onChange={handleChangeCommonRange}
                      disabled={loading}
                    >
                      <option value="">-- Chọn --</option>
                      {RANGE_OPTIONS.map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </select>
                  </div>

                  {commonRangeLabel === RANGE_CUSTOM && (
                    <>
                      <div>
                        <label className="form-label mb-1">Từ ngày</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={commonFromDate}
                          onChange={(e) => setCommonFromDate(e.target.value)}
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <label className="form-label mb-1">Đến ngày</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={commonToDate}
                          onChange={(e) => setCommonToDate(e.target.value)}
                          disabled={loading}
                        />
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleApplyCommonCustomRange}
                        disabled={loading}
                      >
                        Lọc chung
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleClearCommonFilter}
                    disabled={loading}
                  >
                    Xóa lọc chung
                  </button>
                </div>

                {(commonAppliedFrom || commonAppliedTo) && (
                  <div className="filter-hint" style={{ marginTop: 6 }}>
                    Đang lọc chung : {fmtDDMMYYYY(commonAppliedFrom) || '...'} →{' '}
                    {fmtDDMMYYYY(commonAppliedTo) || '...'}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="w-100">
                <h3 className="card-title mb-0 float-none">Báo cáo thống kê</h3>
                <div className="text-muted d-block" style={{ marginTop: 4 }}>
                  Báo cáo tổng quan theo nhóm
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
                <div style={{ minWidth: 220 }}>
                  <label className="form-label mb-1">Khoảng thời gian</label>
                  <select
                    className="form-select form-select-sm"
                    value={rangeLabel}
                    onChange={handleChangeRange}
                    disabled={loading}
                  >
                    <option value="">-- Chọn --</option>
                    {RANGE_OPTIONS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>

                {rangeLabel === RANGE_CUSTOM && (
                  <>
                    <div>
                      <label className="form-label mb-1">Từ ngày</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromDate}
                        onChange={(e) => {
                          if (rangeLabel !== RANGE_CUSTOM) setRangeLabel(RANGE_CUSTOM);
                          setFromDate(e.target.value);
                        }}
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="form-label mb-1">Đến ngày</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={toDate}
                        onChange={(e) => {
                          if (rangeLabel !== RANGE_CUSTOM) setRangeLabel(RANGE_CUSTOM);
                          setToDate(e.target.value);
                        }}
                        disabled={loading}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleApplyCustomRange}
                      disabled={loading}
                    >
                      Lọc
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleClearFilter}
                  disabled={loading}
                >
                  Xóa lọc
                </button>

                {renderFilterHint(appliedFrom, appliedTo)}
              </div>

              {loading ? (
                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                  <Spinner animation="border" size="sm" />
                  <span>Đang tải dữ liệu...</span>
                </div>
              ) : (
                <Table
                  striped
                  bordered
                  hover
                  className="text-center align-middle w-100"
                  style={{ tableLayout: 'fixed' }}
                >
                  <colgroup>
                    <col style={{ width: '70px' }} />
                    <col style={{ width: '260px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '110px' }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th className="sorting">STT</th>
                      <th className="sorting">Team</th>
                      <th className="sorting">Tổng công việc</th>
                      <th className="sorting">Phần mềm</th>
                      <th className="sorting">Xử lý yêu cầu</th>
                      <th className="sorting">Hỗ trợ</th>
                      <th className="sorting">Nhiệm vụ khác</th>
                      <th className="sorting">Chức năng</th>
                    </tr>
                  </thead>

                  {rows && rows.length > 0 ? (
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={`${r.teamId}-${idx}`}>
                          <td>{idx + 1}</td>
                          <td className="text-center">
                            <span className="d-block w-100 text-truncate" title={r.tenTeam}>
                              {r.tenTeam}
                            </span>
                          </td>
                          <td style={{ fontWeight: 700, color: 'red' }}>{r.tongCong}</td>
                          <td>{r.tongPhanMem}</td>
                          <td>{r.tongXuLyYeuCau}</td>
                          <td>{r.tongHoTro}</td>
                          <td>{r.tongNhiemVuKhac}</td>
                          <td>
                            <ul className="nav justify-content-center">
                              <li className="nav-item">
                                <NavLink
                                  to={`${detailBasePath}?teamId=${r.teamId}${
                                    r.tenTeam ? `&tenTeam=${encodeURIComponent(r.tenTeam)}` : ''
                                  }${appliedFrom ? `&From=${encodeURIComponent(appliedFrom)}` : ''}${
                                    appliedTo ? `&To=${encodeURIComponent(appliedTo)}` : ''
                                  }&returnUrl=${currentReturnUrl}`}
                                  exact
                                  className="nav-link"
                                  title="Xem chi tiết"
                                >
                                  <i className="nav-icon font-icon fas fa-eye" />
                                </NavLink>
                              </li>
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr className="txt-center">
                        <td colSpan="8">Không có dữ liệu</td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              )}

              <Row className="mt-3">
                <Col lg={8} className="mb-3 mb-lg-0">
                  <StackedBarTeamChart rows={rows} loading={loading} />
                </Col>
                <Col lg={4}>
                  <WorkStructureDonutChart rows={rows} loading={loading} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="mt-3">
            <BaoCaoChiTietPhatTrienPhanMem
              teamId={null}
              tenTeam=""
              commonFrom={commonAppliedFrom}
              commonTo={commonAppliedTo}
              commonRangeLabel={commonRangeLabel}
              commonVersion={commonVersion}
              readUrlFilter={false}
            />
          </div>

          <div className="mt-3">
            <BaoCaoChiTietXuLyYeuCau
              commonFrom={commonAppliedFrom}
              commonTo={commonAppliedTo}
              commonRangeLabel={commonRangeLabel}
              commonVersion={commonVersion}
            />
          </div>

          <div className="mt-3">
            <BaoCaoChiTietHoTro
              commonFrom={commonAppliedFrom}
              commonTo={commonAppliedTo}
              commonRangeLabel={commonRangeLabel}
              commonVersion={commonVersion}
            />
          </div>

          <div className="mt-3">
            <BaoCaoChiTietNhiemVuKhac
              commonFrom={commonAppliedFrom}
              commonTo={commonAppliedTo}
              commonRangeLabel={commonRangeLabel}
              commonVersion={commonVersion}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default BaoCaoTongQuanTeam;
